/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertIsInstructionWithAccounts,
  containsBytes,
  fixEncoderSize,
  getBytesEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
  SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
  SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
  SolanaError,
  type Address,
  type ClientWithRpc,
  type ClientWithTransactionPlanning,
  type ClientWithTransactionSending,
  type GetAccountInfoApi,
  type GetMultipleAccountsApi,
  type Instruction,
  type InstructionWithData,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  addSelfFetchFunctions,
  addSelfPlanAndSendFunctions,
  type SelfFetchFunctions,
  type SelfPlanAndSendFunctions,
} from '@solana/program-client-core';
import {
  getOracleStateCodec,
  type OracleState,
  type OracleStateArgs,
} from '../accounts';
import {
  getFinalizeInstruction,
  getInitializeOracleInstruction,
  parseFinalizeInstruction,
  parseInitializeOracleInstruction,
  type FinalizeInput,
  type InitializeOracleInput,
  type ParsedFinalizeInstruction,
  type ParsedInitializeOracleInstruction,
} from '../instructions';

export const TRUSTED_ORACLE_PROGRAM_ADDRESS =
  'HhUzN7VvonNUevATyugZUepzxpeEZMXQbV92X2xvsp5m' as Address<'HhUzN7VvonNUevATyugZUepzxpeEZMXQbV92X2xvsp5m'>;

export enum TrustedOracleAccount {
  OracleState,
}

export function identifyTrustedOracleAccount(
  account: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): TrustedOracleAccount {
  const data = 'data' in account ? account.data : account;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([97, 156, 157, 189, 194, 73, 8, 15]),
      ),
      0,
    )
  ) {
    return TrustedOracleAccount.OracleState;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_ACCOUNT,
    { accountData: data, programName: 'trustedOracle' },
  );
}

export enum TrustedOracleInstruction {
  Finalize,
  InitializeOracle,
}

export function identifyTrustedOracleInstruction(
  instruction: { data: ReadonlyUint8Array } | ReadonlyUint8Array,
): TrustedOracleInstruction {
  const data = 'data' in instruction ? instruction.data : instruction;
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([171, 61, 218, 56, 127, 115, 12, 217]),
      ),
      0,
    )
  ) {
    return TrustedOracleInstruction.Finalize;
  }
  if (
    containsBytes(
      data,
      fixEncoderSize(getBytesEncoder(), 8).encode(
        new Uint8Array([144, 223, 131, 120, 196, 253, 181, 99]),
      ),
      0,
    )
  ) {
    return TrustedOracleInstruction.InitializeOracle;
  }
  throw new SolanaError(
    SOLANA_ERROR__PROGRAM_CLIENTS__FAILED_TO_IDENTIFY_INSTRUCTION,
    { instructionData: data, programName: 'trustedOracle' },
  );
}

export type ParsedTrustedOracleInstruction<
  TProgram extends string = 'HhUzN7VvonNUevATyugZUepzxpeEZMXQbV92X2xvsp5m',
> =
  | ({
      instructionType: TrustedOracleInstruction.Finalize;
    } & ParsedFinalizeInstruction<TProgram>)
  | ({
      instructionType: TrustedOracleInstruction.InitializeOracle;
    } & ParsedInitializeOracleInstruction<TProgram>);

export function parseTrustedOracleInstruction<TProgram extends string>(
  instruction: Instruction<TProgram> & InstructionWithData<ReadonlyUint8Array>,
): ParsedTrustedOracleInstruction<TProgram> {
  const instructionType = identifyTrustedOracleInstruction(instruction);
  switch (instructionType) {
    case TrustedOracleInstruction.Finalize: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: TrustedOracleInstruction.Finalize,
        ...parseFinalizeInstruction(instruction),
      };
    }
    case TrustedOracleInstruction.InitializeOracle: {
      assertIsInstructionWithAccounts(instruction);
      return {
        instructionType: TrustedOracleInstruction.InitializeOracle,
        ...parseInitializeOracleInstruction(instruction),
      };
    }
    default:
      throw new SolanaError(
        SOLANA_ERROR__PROGRAM_CLIENTS__UNRECOGNIZED_INSTRUCTION_TYPE,
        {
          instructionType: instructionType as string,
          programName: 'trustedOracle',
        },
      );
  }
}

export type TrustedOraclePlugin = {
  accounts: TrustedOraclePluginAccounts;
  instructions: TrustedOraclePluginInstructions;
};

export type TrustedOraclePluginAccounts = {
  oracleState: ReturnType<typeof getOracleStateCodec> &
    SelfFetchFunctions<OracleStateArgs, OracleState>;
};

export type TrustedOraclePluginInstructions = {
  finalize: (
    input: FinalizeInput,
  ) => ReturnType<typeof getFinalizeInstruction> & SelfPlanAndSendFunctions;
  initializeOracle: (
    input: InitializeOracleInput,
  ) => ReturnType<typeof getInitializeOracleInstruction> &
    SelfPlanAndSendFunctions;
};

export type TrustedOraclePluginRequirements = ClientWithRpc<
  GetAccountInfoApi & GetMultipleAccountsApi
> &
  ClientWithTransactionPlanning &
  ClientWithTransactionSending;

export function trustedOracleProgram() {
  return <T extends TrustedOraclePluginRequirements>(client: T) => {
    return {
      ...client,
      trustedOracle: <TrustedOraclePlugin>{
        accounts: {
          oracleState: addSelfFetchFunctions(client, getOracleStateCodec()),
        },
        instructions: {
          finalize: (input) =>
            addSelfPlanAndSendFunctions(client, getFinalizeInstruction(input)),
          initializeOracle: (input) =>
            addSelfPlanAndSendFunctions(
              client,
              getInitializeOracleInstruction(input),
            ),
        },
      },
    };
  };
}
